import { useCallback, useMemo } from 'react'
import { Button, Empty, Table } from 'antd'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { List, useTable } from '@refinedev/antd'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { ProductSummaryRoot } from '@repo/sdk'

import { bindPaginationToUrl } from '../../shared/hooks/useSyncTableWithUrl'
import { useProductStatusConfig, useSalesChannelConfig } from './productStatusConfig'
import { createProductListColumns } from './ProductListColumns'
import { useDeleteProduct } from './useDeleteProduct'
import { useHideProduct } from './useHideProduct'
import { useUnhideProduct } from './useUnhideProduct'
import { useProductListUrlSync } from './useProductListUrlSync'
import { useProductListFilters, buildProductFilters } from './useProductListFilters'
import { ProductListFilters } from './ProductListFilters'
import { createRowClickHandler } from '@/utils/createRowClickHandler'
import { scrollXForColumns } from '@/components/responsive'
import { confirmPopup } from '@/components/popup'

export function ProductList() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const statusConfig = useProductStatusConfig()
  const salesChannelConfig = useSalesChannelConfig()
  const {
    status: activeStatus,
    name: searchName,
    currentPage: urlPage,
    setStatus,
    setName,
    setPage,
    resetFilters,
  } = useProductListUrlSync()

  const { tableProps, tableQuery, setFilters, currentPage, setCurrentPage } =
    useTable<ProductSummaryRoot>({
      resource: 'product-list',
      pagination: { pageSize: 20, currentPage: urlPage },
      syncWithLocation: false,
      filters: { initial: buildProductFilters(activeStatus, searchName) },
    })

  const { handleRefresh } = useProductListFilters({
    status: activeStatus,
    name: searchName,
    urlPage,
    currentPage,
    setFilters,
    setCurrentPage,
    refetch: () => void tableQuery.refetch(),
  })
  const { deleteProduct } = useDeleteProduct()
  const { hideProduct } = useHideProduct()
  const { unhideProduct } = useUnhideProduct()

  const handleEdit = useCallback((id: string) => void navigate(`/products/${id}/edit`), [navigate])

  const handleDelete = useCallback(
    (id: string) => {
      confirmPopup({
        title: t('common.modals.deleteProductTitle'),
        content: t('common.modals.deleteProductMessage'),
        okText: t('common.buttons.delete'),
        okButtonProps: { danger: true, 'data-testid': 'product-list-delete-confirm-btn' },
        cancelButtonProps: { 'data-testid': 'product-list-delete-cancel-btn' },
        cancelText: t('common.buttons.cancel'),
        onOk: () => deleteProduct(id),
      })
    },
    [t, deleteProduct],
  )

  const handleHide = useCallback(
    (id: string) => {
      confirmPopup({
        title: t('common.modals.hideProductTitle'),
        content: t('common.modals.hideProductMessage'),
        okText: t('common.buttons.confirm'),
        okButtonProps: { 'data-testid': 'product-list-hide-confirm-btn' },
        cancelButtonProps: { 'data-testid': 'product-list-hide-cancel-btn' },
        cancelText: t('common.buttons.cancel'),
        onOk: () => hideProduct(id),
      })
    },
    [t, hideProduct],
  )

  const handleUnhide = useCallback(
    (id: string) => {
      confirmPopup({
        title: t('common.modals.unhideProductTitle'),
        content: t('common.modals.unhideProductMessage'),
        okText: t('common.buttons.confirm'),
        okButtonProps: { 'data-testid': 'product-list-unhide-confirm-btn' },
        cancelButtonProps: { 'data-testid': 'product-list-unhide-cancel-btn' },
        cancelText: t('common.buttons.cancel'),
        onOk: () => unhideProduct(id),
      })
    },
    [t, unhideProduct],
  )

  const columns = useMemo(
    () =>
      createProductListColumns({
        onEdit: handleEdit,
        onDelete: handleDelete,
        onHide: handleHide,
        onUnhide: handleUnhide,
        t,
        statusConfig,
        salesChannelConfig,
      }),
    [handleEdit, handleDelete, handleHide, handleUnhide, t, statusConfig, salesChannelConfig],
  )

  return (
    <List
      title={t('pages.products.listTitle')}
      headerButtons={
        <>
          <Button
            data-testid="product-list-refresh-btn"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          >
            {t('common.buttons.refresh')}
          </Button>
          <Button
            data-testid="product-list-create-btn"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => void navigate('/products/create')}
          >
            {t('common.buttons.createProduct')}
          </Button>
        </>
      }
    >
      <ProductListFilters
        status={activeStatus}
        name={searchName}
        onStatusChange={setStatus}
        onNameChange={setName}
        onReset={resetFilters}
      />
      <Table
        {...tableProps}
        loading={tableQuery.isFetching}
        pagination={bindPaginationToUrl(tableProps.pagination, urlPage, setPage)}
        columns={columns}
        rowKey="id"
        scroll={{ x: scrollXForColumns(columns, 1100) }}
        locale={{
          emptyText: (
            <Empty
              description={t('pages.products.emptyState')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        onRow={(record) => ({
          'data-testid': `product-list-row-${record.id}`,
          ...(record.salesChannel === 'MARKETPLACE' && {
            onClick: createRowClickHandler(() => void navigate(`/products/${record.id}/edit`)),
          }),
          style: { cursor: record.salesChannel === 'MARKETPLACE' ? 'pointer' : 'default' },
        })}
      />
    </List>
  )
}
